import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMarkNotificationsRead, useNotifications } from '@/hooks/queries';
import { useUi } from '@/store/ui';
import { EmptyState, SkeletonRows } from '@/components/ui';
import {
  BellIcon,
  ChatIcon,
  CheckCircleIcon,
  ClockIcon,
  CloseIcon,
  CommentIcon,
  EventIcon,
  SparkleIcon,
  InfoIcon,
  MentionIcon,
  type IconProps,
} from '@/components/ui/icons';
import { cn, relativeTime } from '@/lib/utils';

const KIND_ICONS: Record<string, React.ComponentType<IconProps>> = {
  mention: MentionIcon,
  message: CommentIcon,
  task_assigned: CheckCircleIcon,
  task_due: ClockIcon,
  comment: CommentIcon,
  client_request: ChatIcon,
  meeting_invite: EventIcon,
  celebration: SparkleIcon,
  weekly_recap: SparkleIcon,
  system: InfoIcon,
};

export function NotificationsPanel() {
  const { notificationsOpen, setNotifications, setChat } = useUi();
  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationsRead();
  const navigate = useNavigate();

  /**
   * Opening the panel is the act of reading, so everything is marked read on
   * open. The ids that *were* unread are kept for as long as the panel stays
   * open, so the highlight still shows which ones were new.
   */
  const [justRead, setJustRead] = useState<Set<string>>(new Set());
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;

  useEffect(() => {
    if (!notificationsOpen) {
      setJustRead(new Set());
      return;
    }
    const unread = (data?.items ?? []).filter((n) => !n.read).map((n) => n.id);
    if (unread.length === 0) return;
    setJustRead((current) => new Set([...current, ...unread]));
    markReadRef.current.mutate(undefined);
    // Runs on open, and again if the list arrives after the panel is already up.
  }, [notificationsOpen, data?.items]);

  if (!notificationsOpen) return null;

  const items = data?.items ?? [];

  return (
    <div className="fixed inset-0 z-[65] flex justify-end">
      <div
        className="absolute inset-0 animate-fade-in bg-black/40"
        onClick={() => setNotifications(false)}
      />
      <aside
        role="dialog"
        aria-label="Notifications"
        className="relative flex h-full w-[min(24rem,100vw)] animate-slide-in-right flex-col border-l border-surface-3 bg-surface-1 shadow-overlay"
      >
        <header className="flex items-center gap-2 border-b border-surface-3 px-4 py-3">
          <h2 className="flex-1 text-sm font-semibold text-ink">Notifications</h2>
          <button
            type="button"
            onClick={() => setNotifications(false)}
            aria-label="Close notifications"
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-surface-2 hover:text-ink"
          >
            <CloseIcon />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {isLoading ? (
            <SkeletonRows rows={5} />
          ) : items.length === 0 ? (
            <EmptyState
              icon={BellIcon}
              title="You're all caught up"
              description="Mentions, task assignments and meeting invites land here."
            />
          ) : (
            items.map((n) => {
              const Icon = KIND_ICONS[n.kind] ?? InfoIcon;
              const wasUnread = !n.read || justRead.has(n.id);
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    if (!n.link) return;
                    setNotifications(false);
                    const chatMatch = /^\/chat\/([^/]+)$/.exec(n.link);
                    if (chatMatch) setChat(true, chatMatch[1]);
                    else navigate(n.link);
                  }}
                  className={cn(
                    'flex w-full gap-3 border-b border-surface-2 px-4 py-3 text-left transition-colors hover:bg-surface-2',
                    wasUnread && 'bg-brand-600/5',
                  )}
                >
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-surface-3 text-slate-300">
                    <Icon size="sm" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-slate-100">{n.title}</span>
                    {n.body && (
                      <span className="mt-0.5 block truncate text-xs text-slate-400">{n.body}</span>
                    )}
                    <span className="mt-1 block text-[11px] text-slate-500">
                      {relativeTime(n.createdAt)}
                    </span>
                  </span>
                  {wasUnread && (
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-brand-500">
                      <span className="sr-only">New</span>
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </aside>
    </div>
  );
}
